import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Dropdown, Input, Option } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronUp16Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideLinkButton } from '../../../theme/components/GuideButtons';
import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { ProtocolPicker } from './ProtocolPicker';
import {
  activeFilterCount,
  consumptionStatusLabelKey,
  hasActiveFilters,
  includesProviderFileImport,
  isDateRangeInvalid,
  originLabelKey,
} from '../../../functions/ProviderConsumptions.Functions';
import { fileDisplayName } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  PROVIDER_CONSUMPTION_ORIGINS,
  PROVIDER_CONSUMPTION_STATUSES,
  ProviderConsumptionsFilter,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { IMPORT_ASSIGNMENT_STATUSES, ImportAssignmentStatus } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ProviderFileViewModel } from '../../../models/Entities/AzureFiles/AzureFileUpload';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';

/** Espera después de la última tecla antes de buscar. */
export const SEARCH_DEBOUNCE_MS = 400;
const ALL = '__all__';
/** Clave de localStorage con la última elección de plegado de los filtros (por navegador). */
const FILTERS_OPEN_KEY = 'pcs-filters-open';

export type ProviderConsumptionsFilterPatch = Partial<Omit<ProviderConsumptionsFilter, 'pageNumber' | 'pageSize' | 'sortBy' | 'sortDirection'>>;

export interface ProviderConsumptionsFiltersProps {
  filter: ProviderConsumptionsFilter;
  /** Cambia uno o varios filtros (la sección vuelve a la página 1). */
  onChange: (patch: ProviderConsumptionsFilterPatch) => void;
  onClear: () => void;
  protocols: Protocol[];
  protocolsLoading: boolean;
  /** Archivos importables del proveedor (filtro "Archivo", sólo con origen 5). */
  files: ProviderFileViewModel[];
}

/** Filtros de la grilla unificada: buscar, origen (varios), protocolo, estado, fechas, alarma y, con origen 5, archivo y asignación. */
export const ProviderConsumptionsFilters = ({ filter, onChange, onClear, protocols, protocolsLoading, files }: ProviderConsumptionsFiltersProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.filters.${path}`, options);

  // Búsqueda con espera: el texto se escribe libre y se aplica 400 ms después de la última tecla.
  const [search, setSearch] = useState(filter.searchText ?? '');
  const applied = useRef(filter.searchText ?? '');
  useEffect(() => {
    if ((filter.searchText ?? '') !== applied.current) {
      applied.current = filter.searchText ?? '';
      setSearch(applied.current);
    }
  }, [filter.searchText]);
  useEffect(() => {
    if (search === applied.current) return undefined;
    const timer = setTimeout(() => {
      applied.current = search;
      onChange({ searchText: search });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Plegado del resto de los filtros. Arranca abierto si ya hay filtros aplicados (p. ej. desde la
  // URL); si no, recuerda la última elección del usuario en este navegador.
  const [open, setOpen] = useState<boolean>(() => {
    if (activeFilterCount(filter) > 0) return true;
    try { return window.localStorage.getItem(FILTERS_OPEN_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(FILTERS_OPEN_KEY, open ? '1' : '0'); } catch { /* sin almacenamiento: no pasa nada */ }
  }, [open]);

  const origins = filter.origins ?? [];
  const withImports = includesProviderFileImport(origins);
  const datesInvalid = isDateRangeInvalid(filter.dateFrom, filter.dateTo);
  const fileName = (id: string) => {
    const file = files.find((item) => String(item.id) === id);
    return file ? fileDisplayName(file) : id;
  };

  const activeCount = activeFilterCount(filter);

  return (
    <div className="pcs-filters" data-testid="pcs-filters">
      {/* Siempre a la vista: la búsqueda, cuántos filtros hay aplicados y el plegado del resto */}
      <div className="pcs-filters__bar">
        <Input
          type="search"
          className="pcs-filters__search"
          contentBefore={<Search20Regular />}
          placeholder={tr('searchPlaceholder')}
          aria-label={tr('search')}
          value={search}
          maxLength={200}
          onChange={(_event, data) => setSearch(data.value)}
          data-testid="pcs-filter-search"
        />
        {activeCount > 0 && <GuideCountChip label={tr('activeCount')} count={activeCount} className="pcs-filters__count" />}
        {hasActiveFilters(filter) && (
          <GuideLinkButton
            size="small"
            onClick={() => {
              applied.current = '';
              setSearch('');
              onClear();
            }}
            data-testid="pcs-filter-clear"
          >
            {tr('clear')}
          </GuideLinkButton>
        )}
        <Button
          appearance="subtle"
          size="small"
          className="pcs-filters__toggle"
          icon={open ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
          aria-expanded={open}
          aria-controls="pcs-filters-body"
          onClick={() => setOpen((current) => !current)}
          data-testid="pcs-filters-toggle"
        >
          {open ? tr('less') : tr('more')}
        </Button>
      </div>
      <div id="pcs-filters-body" className="pcs-filters__body" hidden={!open}>
      <GuideFieldGrid>
        <GuideField label={tr('origin')}>
          <Dropdown
            multiselect
            placeholder={tr('allOrigins')}
            value={origins.map((origin) => t(originLabelKey(origin))).join(', ')}
            selectedOptions={origins.map(String)}
            onOptionSelect={(_event, data) => onChange({ origins: data.selectedOptions.map(Number) })}
            data-testid="pcs-filter-origin"
          >
            {PROVIDER_CONSUMPTION_ORIGINS.map((origin) => (
              <Option key={origin} value={String(origin)} text={String(t(originLabelKey(origin)))}>
                {String(t(originLabelKey(origin)))}
              </Option>
            ))}
          </Dropdown>
        </GuideField>
        <GuideField label={tr('protocol')} hint={protocolsLoading ? tr('loadingProtocols') : undefined}>
          <ProtocolPicker
            protocols={protocols}
            value={filter.protocolId}
            onChange={(protocolId) => onChange({ protocolId })}
            placeholder={tr('protocolPlaceholder')}
            emptyText={tr('noProtocols')}
            disabled={protocolsLoading}
            data-testid="pcs-filter-protocol"
          />
        </GuideField>
        <GuideField label={tr('status')}>
          <Dropdown
            value={filter.status !== undefined ? t(consumptionStatusLabelKey(filter.status)) : tr('allStatuses')}
            selectedOptions={[filter.status !== undefined ? String(filter.status) : ALL]}
            onOptionSelect={(_event, data) =>
              onChange({ status: data.optionValue && data.optionValue !== ALL ? Number(data.optionValue) : undefined })
            }
            data-testid="pcs-filter-status"
          >
            <Option value={ALL}>{tr('allStatuses')}</Option>
            {PROVIDER_CONSUMPTION_STATUSES.map((status) => (
              <Option key={status} value={String(status)} text={String(t(consumptionStatusLabelKey(status)))}>
                {String(t(consumptionStatusLabelKey(status)))}
              </Option>
            ))}
          </Dropdown>
        </GuideField>
        <GuideField label={tr('dateFrom')}>
          <Input type="date" value={filter.dateFrom ?? ''} onChange={(_event, data) => onChange({ dateFrom: data.value || undefined })} data-testid="pcs-filter-date-from" />
        </GuideField>
        <GuideField label={tr('dateTo')} error={datesInvalid ? tr('invalidDates') : undefined}>
          <Input type="date" value={filter.dateTo ?? ''} onChange={(_event, data) => onChange({ dateTo: data.value || undefined })} data-testid="pcs-filter-date-to" />
        </GuideField>
        {withImports && (
          <GuideField label={tr('file')}>
            <Dropdown
              value={filter.providerFileId ? fileName(filter.providerFileId) : tr('allFiles')}
              selectedOptions={[filter.providerFileId ?? ALL]}
              onOptionSelect={(_event, data) => onChange({ providerFileId: data.optionValue && data.optionValue !== ALL ? data.optionValue : undefined })}
              data-testid="pcs-filter-file"
            >
              <Option value={ALL}>{tr('allFiles')}</Option>
              {files.map((file) => (
                <Option key={String(file.id)} value={String(file.id)}>
                  {fileDisplayName(file)}
                </Option>
              ))}
            </Dropdown>
          </GuideField>
        )}
        {withImports && (
          <GuideField label={tr('assignment')}>
            <Dropdown
              value={filter.assignmentStatus ? t(`providerConsumptionImport.status.${filter.assignmentStatus}`) : tr('allAssignments')}
              selectedOptions={[filter.assignmentStatus ?? ALL]}
              onOptionSelect={(_event, data) =>
                onChange({ assignmentStatus: data.optionValue && data.optionValue !== ALL ? (data.optionValue as ImportAssignmentStatus) : undefined })
              }
              data-testid="pcs-filter-assignment"
            >
              <Option value={ALL}>{tr('allAssignments')}</Option>
              {IMPORT_ASSIGNMENT_STATUSES.map((status) => (
                <Option key={status} value={status} text={String(t(`providerConsumptionImport.status.${status}`))}>
                  {String(t(`providerConsumptionImport.status.${status}`))}
                </Option>
              ))}
            </Dropdown>
          </GuideField>
        )}
        <GuideField label={tr('alarm')}>
          <Checkbox checked={!!filter.hasAlarm} onChange={(_event, data) => onChange({ hasAlarm: !!data.checked })} label={tr('onlyAlarm')} data-testid="pcs-filter-alarm" />
        </GuideField>
      </GuideFieldGrid>
      </div>
    </div>
  );
};

export default ProviderConsumptionsFilters;
